import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

export function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [functionalCookies, setFunctionalCookies] = useState(true);
  const [analyticsCookies, setAnalyticsCookies] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Check for existing preferences
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      setOpen(true);
    } else {
      try {
        const savedPreferences = JSON.parse(consent);
        setFunctionalCookies(savedPreferences.functional);
        setAnalyticsCookies(savedPreferences.analytics);
      } catch (error) {
        console.error("Error parsing saved preferences:", error);
        setOpen(true);
      }
    }
  }, []);

  const savePreferences = async (preferences: { functional: boolean; analytics: boolean }) => {
    try {
      localStorage.setItem(
        "cookieConsent",
        JSON.stringify({
          ...preferences,
          timestamp: new Date().toISOString(),
        })
      );
      return true;
    } catch (error) {
      console.error("Error saving preferences:", error);
      return false;
    }
  };

  const handleAcceptAll = async () => {
    setIsSaving(true);
    const saved = await savePreferences({ functional: true, analytics: true });
    
    if (saved) {
      toast({
        title: "Preferences Saved",
        description: "All cookie preferences have been accepted.",
      });
      setOpen(false);
    } else {
      toast({
        title: "Error",
        description: "Failed to save preferences. Please try again.",
        variant: "destructive",
      });
    }
    setIsSaving(false);
  };

  const handleSavePreferences = async () => {
    setIsSaving(true);
    const saved = await savePreferences({
      functional: functionalCookies,
      analytics: analyticsCookies,
    });

    if (saved) {
      toast({
        title: "Preferences Saved",
        description: "Your cookie preferences have been saved successfully.",
      });
      setOpen(false);
    } else {
      toast({
        title: "Error",
        description: "Failed to save preferences. Please try again.",
        variant: "destructive",
      });
    }
    setIsSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[500px]" aria-labelledby="cookie-preferences-title">
        <DialogHeader>
          <DialogTitle id="cookie-preferences-title">Cookie Preferences</DialogTitle>
          <DialogDescription>
            To provide the best experiences, we use technologies like cookies to store and/or access
            device information. Consenting to these technologies will allow us to process data such as
            browsing behavior or unique IDs on this site. Not consenting or withdrawing consent may
            adversely affect certain features and functions.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="flex items-start space-x-4">
            <Checkbox
              id="functional"
              checked={functionalCookies}
              onCheckedChange={(checked) => setFunctionalCookies(checked as boolean)}
              aria-label="Functional Cookies"
              disabled={isSaving}
            />
            <div className="space-y-1">
              <Label htmlFor="functional" className="font-medium">
                Functional Cookies
              </Label>
              <p className="text-sm text-muted-foreground">
                Essential for the website to function properly. These cannot be disabled.
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <Checkbox
              id="analytics"
              checked={analyticsCookies}
              onCheckedChange={(checked) => setAnalyticsCookies(checked as boolean)}
              aria-label="Analytics Cookies"
              disabled={isSaving}
            />
            <div className="space-y-1">
              <Label htmlFor="analytics" className="font-medium">
                Analytics Cookies
              </Label>
              <p className="text-sm text-muted-foreground">
                Help us improve our website by collecting and reporting usage information.
              </p>
            </div>
          </div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            onClick={handleSavePreferences}
            disabled={isSaving}
            aria-label="Save Cookie Preferences"
          >
            {isSaving ? "Saving..." : "Save Preferences"}
          </Button>
          <Button 
            onClick={handleAcceptAll}
            disabled={isSaving}
            aria-label="Accept All Cookies"
          >
            {isSaving ? "Accepting..." : "Accept All"}
          </Button>
          <div className="text-xs text-muted-foreground text-center sm:text-left mt-4 sm:mt-0">
            By using our site, you acknowledge that you have read our{" "}
            <Link to="/privacy" className="underline hover:text-primary">
              Privacy Policy
            </Link>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}